import React, { useState } from "react";
import { format, parse } from "date-fns";
import { Calendar as CalendarIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

/**
 * ISO (YYYY-MM-DD) date picker rendered as a Popover + Calendar.
 *
 * Props:
 *  - value (string, ISO YYYY-MM-DD) — current value
 *  - onChange (fn, ISO YYYY-MM-DD) — called when a date is picked
 *  - testId (string) — for the trigger button
 *  - placeholder (string)
 *  - disabled (bool)
 *  - className (string)
 */
export default function DatePickerField({ value, onChange, testId, placeholder = "Pick a date", disabled, className = "" }) {
  const [open, setOpen] = useState(false);
  const parsed = value ? parse(value, "yyyy-MM-dd", new Date()) : null;
  const valid = parsed && !Number.isNaN(parsed.getTime());

  const handleSelect = (d) => {
    if (!d) return;
    onChange(format(d, "yyyy-MM-dd"));
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          disabled={disabled}
          className={cn(
            "rounded-sm justify-start font-normal mono-num",
            !valid && "text-muted-foreground",
            className,
          )}
          data-testid={testId}
        >
          <CalendarIcon className="w-4 h-4 mr-2 shrink-0" />
          {valid ? format(parsed, "EEE, MMM d, yyyy") : placeholder}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={valid ? parsed : undefined}
          onSelect={handleSelect}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  );
}
